import { NgClass, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterContentChecked, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { first, pairwise } from 'rxjs/operators';
import { AdminService } from '../admin/admin.service';
import { AuthGuard } from '../auth/auth.guard';
import { DataRulesDataService } from '../data-rules/data-rules-data.service';
import { AllPrimaryEntitiesNames, AllPrimaryEntitiesTypes, DataRuleId, DefaultEntities } from '../data-rules/data-rules-model';
import { CustomOverlayForDirective, ICustomPositioningConfig } from '../data-rules/lib/custom-component-overlay.directive';
import { MetaDataService } from '../data-rules/meta-data.service';
import { ChainedRuleGroupsScreenComponent } from '../data-rules/rule/rule-chain/chained-rules-groups-screen/chained-rule-groups-screen.component';
import { RuleChainsDataService } from '../data-rules/rule/rule-chain/rule-chain-data.service';
import {
  ENRICH_CREDIT_ICON_FULL,
  EnrichmentCreditService,
  enrichmentCreditIcon,
  enrichmentCreditPillTooltip,
  enrichmentCreditsRemaining,
  IEnrichmentCreditBalance
} from '../enrichment-settings/enrichment-credit.service';
import { CompactNumberValuesPipe } from '../kyloe-pipes.pipe';
import { RippleDirective } from '../ripple.directive';
import { DataToolsApps, DataToolsAppsTitles } from '../shared/DataToolsApps';
import { DuplikitMergeService } from '../shared/services/duplikit-merge.service';
import { LoadingSnackbarService, SnackMessageNames } from '../shared/services/loading-snackbar.service';
import { UtilsService } from '../shared/services/utils.service';
import { HeaderServiceDirective } from './header.service';

async function* restoreAllSpecifiedChains(
  ids: DataRuleId[],
  compareInternalDataRuleIds,
  deleteChain, restoreChain,
  getViewState, setViewState
) {
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (compareInternalDataRuleIds(id, 0)) {
      await deleteChain(id).toPromise();
      yield id;
    } else {
      await restoreChain(id).toPromise().then(() => {
        setViewState(id, Object.assign(getViewState(id), {dirty: false}));
      });
      yield id;
    }
  }
}

@Component({
  selector: 'app-header',
  imports: [
    RouterLink,
    NgIf,
    NgClass,
    MatTooltip,
    ChainedRuleGroupsScreenComponent,
    CustomOverlayForDirective,
    RippleDirective,
    CompactNumberValuesPipe
  ],
  templateUrl: './header.component.html'
})
export class HeaderComponent implements OnInit, AfterContentChecked {

  disabledDB: boolean;
  app: string;
  isAppEnabled: boolean;
  schedulePopup: boolean;
  currentSection: string;
  maxNumberOfAutomatedRulesExceeded: boolean;
  corporationName: string;
  public currentChildSection: string;

  creditBalance: IEnrichmentCreditBalance | null = null;
  creditsExhausted = false;
  creditsRemaining = 0;
  creditIcon = ENRICH_CREDIT_ICON_FULL;
  creditTooltip = '';

  routeParamEntity: AllPrimaryEntitiesNames;
  chainedRulesEnabled: boolean;
  areChainsOpened: boolean;
  routeParamChainIds: DataRuleId[];

  chainsOverlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: -24,
    offsetX: -118
  };

  @ViewChild('chainsTrigger')
  chainsTriggerRef: ElementRef<HTMLElement>;
  @ViewChild('chainGroups')
  chainsComponent: ChainedRuleGroupsScreenComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private headerService: HeaderServiceDirective,
    private http: HttpClient,
    private adminService: AdminService,
    private duplikitMergeService: DuplikitMergeService,
    private authGuard: AuthGuard,
    private metaDataService: MetaDataService,
    public ruleChainsDataService: RuleChainsDataService,
    public dataRulesDataService: DataRulesDataService,
    public loadingSnackbarService: LoadingSnackbarService,
    public utilsService: UtilsService,
    private enrichmentCreditService: EnrichmentCreditService
  ) {
  }

  get isInLiteMode() {
    return this.adminService.isInLiteMode();
  }

  get isEnrichmentSection(): boolean {
    return this.currentSection === DataToolsAppsTitles.ENRICHMENT_SETTINGS;
  }

  get isCreditHistorySection(): boolean {
    return this.currentChildSection === DataToolsAppsTitles.ENRICHMENT_CREDIT_HISTORY;
  }

  ngOnInit(): void {
    this.headerService.maxNumberOfAutomatedRulesExceeded.subscribe(isExceeded => {
      this.maxNumberOfAutomatedRulesExceeded = isExceeded;
    });
    this.adminService.corporationName.subscribe(name => {
      this.corporationName = name;
    });

    this.headerService.changeCurrentSection.subscribe(title => {
      this.currentSection = title;
      if (title === DataToolsAppsTitles.ENRICHMENT_SETTINGS) {
        this.loadCreditBalance();
      }
    });

    this.headerService.changeCurrentChildSection.subscribe(title => {
      this.currentChildSection = title;
      if (title === DataToolsAppsTitles.ENRICHMENT_CREDIT_HISTORY) {
        this.loadCreditBalance();
      }
    });

    if (this.currentSection === 'DataRules') {
      this.ruleChainsDataService.initialize(true).subscribe();
    }

    this.route.queryParams.pipe(first()).subscribe(queryParams => this.onRouteChange(null, queryParams));
    this.route.queryParams.pipe(pairwise())
      .subscribe(([previousQueryParams, queryParams]) => this.onRouteChange(previousQueryParams, queryParams));
    this.route.queryParamMap.subscribe(params => {
      this.routeParamEntity = params.get('entity') as AllPrimaryEntitiesNames;
    });
  }

  onRouteChange(previousParams, newParams): void {

    // Getting the entity and rule query params
    // 'entity' param expected to be undefined OR 'EntityName'-like string
    const routeEntityParam = newParams['entity'];
    this.routeParamEntity = routeEntityParam;

    // 'chain' param expected to be undefined OR '' OR 'id,id,id...'-like string
    const routeChainParam = newParams['chain'];
    this.routeParamChainIds = routeChainParam ? routeChainParam.split(',') : [];
    this.areChainsOpened = typeof routeChainParam === 'string';
  }

  ngAfterContentChecked() {
    // TODO: This could be cleaner, matching up against the ENUM
    this.adminService.finishedLoading$.subscribe(() => {
      this.app = this.authGuard.app;
      switch (this.app) {
        case 'duplikit' :
          this.isAppEnabled = this.appEnabled(DataToolsApps.DUPLIKIT);
          break;
        case 'datacleaner' :
          this.isAppEnabled = this.appEnabled(DataToolsApps.DATA_CLEANER);
          break;
        case 'datarules' :
          this.isAppEnabled = this.appEnabled(DataToolsApps.DATARULES);
          break;
        case 'history' :
          this.isAppEnabled = this.appEnabled(DataToolsApps.DATARULES);
          break;
        case undefined :
          // Landing Page
          this.isAppEnabled = true;
          break;
      }
      this.chainedRulesEnabled = this.appEnabled(DataToolsApps.DATARULES_CHAINED_RULES);
    });
  }

  appEnabled(pca: DataToolsApps): boolean {
    return this.adminService.checkAppEnabled(pca);
  }

  userStatus(): boolean {
    return this.adminService.userStatus.authorizedUser;
  }

  getCurrentSection() {
    switch (this.currentSection) {
      case 'DupliKit': {
        return this.currentSection.toLowerCase();
      }
      case 'Settings':
      case 'DataCleaner':
      case 'DataRules': {
        this.currentChildSection = '';
        return this.currentSection.toLowerCase();
      }
      case 'History':
        if (this.currentChildSection)
          return 'history/' + this.currentChildSection.toLowerCase();
        else
          return 'history';
      default: {
        return '';
      }
    }
  }

  private loadCreditBalance(): void {
    this.enrichmentCreditService.getCreditBalance().subscribe({
      next: balance => {
        this.creditBalance = balance;
        this.creditsRemaining = enrichmentCreditsRemaining(balance);
        this.creditsExhausted = this.creditsRemaining <= 0;
        this.creditIcon = enrichmentCreditIcon(this.creditsRemaining);
        this.creditTooltip = enrichmentCreditPillTooltip(this.creditsRemaining);
      }
    });
  }

  datarulesSchedulingMessage(): string {
    if (this.appEnabled(DataToolsApps.DATARULES_SCHEDULING)) {
      return 'Automation Enabled';
    } else {
      return 'Automated Rules coming soon...';
    }
  }

  private onSchedulePopupToggle(isOpened: boolean) {
    this.schedulePopup = isOpened;
  }

  shouldChainsClose(): boolean {
    const isAnyUnsavedChain = this.ruleChainsDataService.allChains.some(
      chain => {
        return this.chainsComponent.getViewState(chain.id).dirty;
      }
    );
    return isAnyUnsavedChain
      ? window.confirm('Unsaved changes. Do you want to continue?')
      : true;
  }

  async onChainTriggerClicked(closing?: true): Promise<void> {
    if (!closing && !this.areChainsOpened && !this.ruleChainsDataService.loading) {
      this.navigateToChains();
      return;
    }
    if (closing && this.areChainsOpened) {
      this.checkForUnsavedChainsAndRestore();
      this.navigateToDataRules(this.routeParamEntity);
    }
  }

  onChainClicked(id): void {
    let isSelectedChainUnsaved = false;
    const isSelectedChain = this.routeParamChainIds.findIndex(chainId => this.utilsService.compareInternalDataRuleIds(chainId, id)) > -1;
    let newIDs: DataRuleId[];
    if (isSelectedChain) {
      isSelectedChainUnsaved =
        this.utilsService.compareInternalDataRuleIds(id, 0)
        || this.ruleChainsDataService.allChains.some(
          chain => {
            return this.utilsService.compareInternalDataRuleIds(chain.id, id) && this.chainsComponent.getViewState(chain.id).dirty;
          }
        );
      newIDs = this.routeParamChainIds.filter(chainId => `${chainId}` !== `${id}`);
    } else {
      newIDs = [...this.routeParamChainIds, id];
    }

    if (isSelectedChainUnsaved) {
      if (!window.confirm('Unsaved changes. Do you want to continue?'))
        return;
      if (this.utilsService.compareInternalDataRuleIds(id, 0)) {
        this.ruleChainsDataService.deleteChain(id).subscribe();
      } else {
        this.ruleChainsDataService.restoreChain(id).subscribe(() => {
          this.chainsComponent.setViewState(id, Object.assign(this.chainsComponent.getViewState(id), {dirty: false}));
        });
      }
    }
    this.navigateToChains(newIDs);
  }

  private navigateToDataRules(entity?: AllPrimaryEntitiesNames, rules?: DataRuleId[]): void {
    if (!entity || !AllPrimaryEntitiesTypes.isContains(entity) || !this.metaDataService.isPrimaryEntity(entity)) {
      this.router.navigate(['datarules'], {queryParams: {entity: DefaultEntities.DEFAULT_DATARULES_ENTITY}});
      return;
    }
    const rulesParam = rules && rules.length > 0 ? rules.join(',') : undefined;
    const chainsParam = undefined;
    this.router.navigate(['datarules'], {queryParams: {entity, rule: rulesParam, chain: chainsParam}});
  }

  private navigateToDataRulesFromChainGroup(entity: AllPrimaryEntitiesNames, rules?: DataRuleId[]): void {
    this.chainsComponent.closed.emit();
    // required timeout to wait for pop-up got destroyed
    setTimeout(() => {
      if (!this.chainsComponent.popupComponent) {
        this.navigateToDataRules(entity, rules);
      }
    }, 100);
  }

  navigateToChains(chains?: DataRuleId[]): void {
    const entityParam = this.routeParamEntity;
    const rulesParam = undefined;
    const chainsParam = chains && chains.length > 0 ? chains.join(',') : '';
    this.router.navigate(['datarules'], {queryParams: {entity: entityParam, rule: rulesParam, chain: chainsParam}});
  }

  async checkForUnsavedChainsAndRestore(): Promise<void> {
    const allUnsavedChainIds = this.ruleChainsDataService.allChains.reduce<DataRuleId[]>(
      (acc, chain) => {
        if (this.chainsComponent.getViewState(chain.id).dirty)
          acc.push(chain.id);
        return acc;
      }, []
    );
    if (allUnsavedChainIds.length > 0) {
      this.loadingSnackbarService.show(SnackMessageNames.RESTORING_CHAINS);
      const generator = restoreAllSpecifiedChains(
        allUnsavedChainIds,
        this.utilsService.compareInternalDataRuleIds,
        this.ruleChainsDataService.deleteChain.bind(this.ruleChainsDataService),
        this.ruleChainsDataService.restoreChain.bind(this.ruleChainsDataService),
        this.chainsComponent.getViewState.bind(this.chainsComponent),
        this.chainsComponent.setViewState.bind(this.chainsComponent),
      );
      for await (const id of generator) {
        console.log(`${id} chain restored`);
      }
      this.loadingSnackbarService.hide(SnackMessageNames.RESTORING_CHAINS);
    }
  }

}
